import {
  IonContent, IonHeader, IonGrid, IonPage, 
  IonRow, IonCol, IonSegmentButton, IonTitle, 
  IonToolbar, IonLabel, IonSegment, IonInput,
  IonButton, IonItem, IonModal, IonFooter, 
  IonButtons, IonAlert, IonToast, IonIcon,
  IonActionSheet, useIonViewWillLeave
} from '@ionic/react';
import { warningOutline, close } from 'ionicons/icons';

import {
  useTranslationData, 
  useCalculationValidation,
  useSavedCalculations,
  useInputValidation
} from '../hooks/calculatorHooks';

import {
  createNumericInputHandler,
  toggleSignHandler,
  updateCalculations,
  calculateResults,
  type CalculationState,
  type CalculationStateSetters,
  type CalculationContext
} from '../utils/calculationUtils';

import { useAppPreferences } from '../hooks/preferencesHooks';
import { CALCULATION_CONFIG } from '../utils/constants';
import { scrollToTop, scrollToBottom, shouldAutoScroll } from '../utils/scrollUtils';
import { provideFeedback } from '../utils/feedbackUtils';
import { isInSpecialGorRange, requiresBallastedTrackHandling, calculateSpecialGorAdjustments } from '../utils/gorUtils';
import './styles/Calculator.css';
import OnlineIndicator from '../components/OnlineIndicator';
import { useState, useEffect, useRef } from 'react';

const Calculator: React.FC = () => {
  // Get user preferences
  const { preferences, isLoading } = useAppPreferences();
  const initialLoadRef = useRef<boolean>(true);
  
  // Refs for smooth scrolling
  const contentRef = useRef<HTMLIonContentElement>(null);
  
  // Default to B Division, but will be updated from preferences
  const [ division     , setDivision      ] = useState<'A Division' | 'B Division'>('B Division');
  const [ H            , setH             ] = useState<number | null>(null);
  const [ D            , setD             ] = useState<number | null>(null);
  const [ MO           , setMO            ] = useState<number | null>(null);
  const [ SUPER        , setSUPER         ] = useState<number | null>(null);
  const [ trackType    , setTrackType     ] = useState<'curve' | 'tangent'>('curve');
  const [ direction    , setDirection     ] = useState<'inside' | 'outside'>('inside');
  const [ R            , setR             ] = useState<number>(0);
  const [ SE           , setSE            ] = useState<number>(0);
  const [ EE           , setEE            ] = useState<number>(0);
  const [ CE           , setCE            ] = useState<number>(0);
  const [ LLLEMinReq   , setLLLEMinReq    ] = useState<number>(0);
  const [ LLLEClearance, setLLLEClearance ] = useState<number>(0);
  const [ clearance    , setClearance     ] = useState<number>(0);
  const [ hStep        , ] = useState<string>(CALCULATION_CONFIG.STEP_SIZE);
  const [ dStep        , ] = useState<string>(CALCULATION_CONFIG.STEP_SIZE);
  const [ moStep       , ] = useState<string>(CALCULATION_CONFIG.STEP_SIZE);
  const [ superStep    , ] = useState<string>(CALCULATION_CONFIG.STEP_SIZE);
  const [ state        , setState         ] = useState<'input' | 'results'>('input');
  const [ isSpecialGorRange, setIsSpecialGorRange ] = useState<boolean>(false);
  const [ hasStartedInput, setHasStartedInput ] = useState<boolean>(false);

  // Save calculation states
  const [ showSaveDialog, setShowSaveDialog ] = useState<boolean>(false);
  const [ calculationName, setCalculationName ] = useState<string>('');
  const [ saveTrack, setSaveTrack ] = useState<string>('');

  // B Division specific alert states
  const [ showBallastedAlert, setShowBallastedAlert ] = useState<boolean>(false);
  const [ showVerticalCurveAlert, setShowVerticalCurveAlert ] = useState<boolean>(false);
  const [ showAdjustmentInfoAlert, setShowAdjustmentInfoAlert ] = useState<boolean>(false);
  const [ showSpecialGorAlert, setShowSpecialGorAlert ] = useState<boolean>(false);
  const [ isBallasted, setIsBallasted ] = useState<boolean | null>(null);
  const [ hasVerticalCurve, setHasVerticalCurve ] = useState<boolean | null>(null);

  // TOR/GOR validation toast state
  const [ showTorGorToast, setShowTorGorToast ] = useState<boolean>(false);

  // Action sheet state for reset options
  const [ showResetActionSheet, setShowResetActionSheet ] = useState<boolean>(false);

  // Get saved calculations hook
  const { 
    savedCalculations, 
    saveCalculation,
    setStorageWarning
  } = useSavedCalculations();

  // Get comprehensive input validation
  const validation = useInputValidation({
    H, D, MO, SUPER, division, trackType, isSpecialGorRange, hasStartedInput
  });

  // Get translation data
  const { 
    A_height_to_clearance, 
    B_height_to_clearance,
    A_above_gor_to_height,
    B_above_gor_to_height
  } = useTranslationData();

  // Check if GOR value is in special range
  useEffect(() => {
    const wasSpecialRange = isSpecialGorRange;
    const isNowSpecialRange = isInSpecialGorRange(D);
    
    if (isNowSpecialRange !== isSpecialGorRange) {
      setIsSpecialGorRange(isNowSpecialRange);
      
      // Reset MO and SUPER when entering the special GOR range
      if (isNowSpecialRange && !wasSpecialRange) {
        setMO(null);
        setSUPER(null);
      }
    }
  }, [D, isSpecialGorRange]);

  // Show/hide TOR/GOR validation toast
  useEffect(() => {
    setShowTorGorToast(validation.torGorError.hasError);
  }, [validation.torGorError.hasError]);

  // Hide toast when leaving the Calculator tab
  useIonViewWillLeave(() => {
    setShowTorGorToast(false);
  });

  // Check if the inputs are sufficient for calculation
  const calculateEnabled = useCalculationValidation({
    H, D, MO, SUPER, trackType, isSpecialGorRange
  });

  // Create the updateCalcs function using the utility
  const updateCalcs = (): void => {
    // Only check for special conditions if we're calculating (not resetting)
    if (state === 'input' && requiresBallastedTrackHandling(D)) {
      // Show the ballasted track alert first
      setShowBallastedAlert(true);
      return; // Don't proceed with calculation yet
    }

    const currentState: CalculationState = { state, H, D, MO, SUPER };

    const setters: CalculationStateSetters = {
      setState, setH, setD, setMO, setSUPER,
      setR, setSE, setEE, setCE, setLLLEMinReq, 
      setLLLEClearance, setClearance
    };

    const context: CalculationContext = {
      trackType, direction, division,
      A_height_to_clearance, B_height_to_clearance,
      A_above_gor_to_height, B_above_gor_to_height
    };

    const wasCalculating = state === 'input';
    
    updateCalculations(currentState, setters, context);
    
    // If this is a reset action (state === 'results'), also reset the alert-related states
    if (state === 'results') {
      setIsBallasted(null);
      setHasVerticalCurve(null);
      setShowAdjustmentInfoAlert(false);
      setHasStartedInput(false); // Reset validation tracking
      validation.clearAllValidation(); // Clear all validation states
      
      // Auto-scroll to top after reset if content overflows viewport
      if (shouldAutoScroll(contentRef)) {
        setTimeout(() => scrollToTop(contentRef), 100);
      }
    } else if (wasCalculating) {
      // Auto-scroll to bottom after calculation if content overflows viewport
      if (shouldAutoScroll(contentRef)) {
        setTimeout(() => scrollToBottom(contentRef), 100);
      }
    }
  };

  // Handle calculate button click with improved validation system
  const handleCalculateClick = (): void => {
    // Mark as started to trigger validation
    if (!hasStartedInput) {
      setHasStartedInput(true);
    }

    const hasBasicValidationErrors = (state === 'input' && !calculateEnabled);
    const hasTorGorValidationErrors = validation.torGorError.hasError;
    
    if (hasBasicValidationErrors || hasTorGorValidationErrors) {
      // Show Calculate button specific errors and shakes
      validation.showCalculateErrors(validation.missingFields, hasTorGorValidationErrors);
      
      return; // Don't proceed with calculation
    }
    
    // Proceed with normal calculation
    updateCalcs();
  };

  // Helper functions for toggle warning flashes
  const triggerToggleWarningH = (): void => {
    validation.triggerFieldShake('H');
    provideFeedback('Cannot toggle sign - value would be out of bounds');
  };

  const triggerToggleWarningD = (): void => {
    validation.triggerFieldShake('D');
    provideFeedback('Cannot toggle sign - value would be out of bounds');
  };
  
  // Handle ballasted track answer
  const handleBallastedResponse = (isTrackBallasted: boolean): void => {
    // Only process if we're in input state
    if (state === 'input') {
      setIsBallasted(isTrackBallasted);
      setShowBallastedAlert(false);
      
      // Show the vertical curve alert next
      setShowVerticalCurveAlert(true);
    }
  };
  
  // Handle vertical curve answer
  const handleVerticalCurveResponse = (hasVertCurve: boolean): void => {
    // Only process if we're in input state
    if (state === 'input') {
      setHasVerticalCurve(hasVertCurve);
      setShowVerticalCurveAlert(false);
      
      // Only show the adjustment information alert if at least one adjustment will be made
      // (i.e., if either ballasted track or vertical curve is true)
      if (isBallasted === true || hasVertCurve === true) {
        setShowAdjustmentInfoAlert(true);
      } else {
        // If no adjustments are needed, proceed directly with calculation
        handleAdjustmentInfoContinue();
      }
    }
  };

  // Handle adjustment information acknowledgment and proceed with calculation
  const handleAdjustmentInfoContinue = (): void => {
    setShowAdjustmentInfoAlert(false);
    
    // Now proceed with the calculation
    const currentState: CalculationState = { state, H, D, MO, SUPER };

    const setters: CalculationStateSetters = {
      setState, setH, setD, setMO, setSUPER,
      setR, setSE, setEE, setCE, setLLLEMinReq, 
      setLLLEClearance, setClearance
    };

    const context: CalculationContext = {
      trackType, direction, division,
      A_height_to_clearance, B_height_to_clearance,
      A_above_gor_to_height, B_above_gor_to_height
    };
    
    // Calculate adjustments
    const adjustments = calculateSpecialGorAdjustments(isBallasted, hasVerticalCurve);
    
    // Perform the calculations
    const results = calculateResults({
      trackType, 
      direction, 
      division, 
      H: H ?? 0, 
      D: D ?? 0, 
      MO: MO ?? 0, 
      SUPER: SUPER ?? 0,
      A_height_to_clearance, 
      B_height_to_clearance,
      A_above_gor_to_height, 
      B_above_gor_to_height
    });
    
    // Apply adjustments for -28.25 GOR case
    if (requiresBallastedTrackHandling(D)) {
      const adjustedLLLEClearance = results.llleclearance + adjustments;
      
      // Set state with adjusted values
      setState('results');
      setR(results.r);
      setSE(results.se);
      setEE(results.ee);
      setCE(results.ce);
      setLLLEMinReq(results.minreq);
      setLLLEClearance(adjustedLLLEClearance);
      
      // Calculate the clearance using H - adjusted LLLE clearance
      const adjustedClearance = (typeof H === 'number' ? H : 0) - adjustedLLLEClearance;
      setClearance(adjustedClearance);
    } else {
      // Use normal calculation flow
      updateCalculations(currentState, setters, context);
    }
    
    // Auto-scroll to bottom after calculation if content overflows viewport
    if (shouldAutoScroll(contentRef)) {
      setTimeout(() => scrollToBottom(contentRef), 100);
    }
  };

  // Generate adjustment information message
  const getAdjustmentMessage = (): string => {
    const adjustments: string[] = [];
    
    if (isBallasted === true) {
      adjustments.push('3-1/4" is added to the minimum required height because this is on ballasted track');
    }
    
    if (hasVerticalCurve === true) {
      adjustments.push('1-3/4" is added to the minimum required height because there is a vertical curve on the track');
    }
    
    return adjustments.length === 0 
      ? 'No adjustments will be made to the minimum required height.'
      : adjustments.length === 1
        ? `Note: ${adjustments[0]}.`
        : `${adjustments.join(' and ')}.`;
  };

  // Handle opening the save dialog
  const openSaveDialog = () => {
    setCalculationName('');
    setSaveTrack('');
    setShowSaveDialog(true);
  };

  // Handle "Edit Measurements" - clear calculations but preserve measurements
  const handleEditMeasurements = (): void => {
    // Switch to input mode to make fields editable
    setState('input');
    
    // Clear only the calculation results, keep input values as they are
    setR(0);
    setSE(0);
    setEE(0);
    setCE(0);
    setLLLEMinReq(0);
    setLLLEClearance(0);
    setClearance(0);
    
    // Reset alert-related states
    setIsBallasted(null);
    setHasVerticalCurve(null);
    setShowAdjustmentInfoAlert(false);
    
    // Auto-scroll to top after switching to edit mode
    if (shouldAutoScroll(contentRef)) {
      setTimeout(() => scrollToTop(contentRef), 100);
    }
  };

  // Handle reset button click - show action sheet if in results mode
  const handleResetClick = (): void => {
    if (state === 'results') {
      setShowResetActionSheet(true);
    } else {
      // If in input mode, just run calculation
      handleCalculateClick();
    }
  };

  // Handle saving calculation
  const handleSaveCalculation = async () => {
    // Create inputs and results objects to save
    const inputs = {
      division,
      trackType,
      direction,
      H: H || 0,
      D: D || 0,
      MO: MO || 0,
      SUPER: SUPER || 0
    };
     
    const results = {
      r: R,
      se: SE,
      ee: EE,
      ce: CE,
      minreq: LLLEMinReq,
      llleclearance: LLLEClearance,
      clearance
    };

    // Use the default name if none provided
    const name = calculationName.trim() || `Calculation ${savedCalculations.length + 1}`;
    
    // Get track name (may be empty string)
    const trackToSave = saveTrack.trim();

    // Save the calculation using the hook
    const result = await saveCalculation(
      inputs, 
      results, 
      name, 
      trackToSave,
      // Pass ballasted track and vertical curvature values if this is a -28.25 GOR calculation
      requiresBallastedTrackHandling(inputs.D) ? isBallasted || undefined : undefined,
      requiresBallastedTrackHandling(inputs.D) ? hasVerticalCurve || undefined : undefined
    );

    if (result.success) {
      // Reset the dialog state
      setShowSaveDialog(false);
      setCalculationName('');
    } else {
      // Show error if save failed
      setStorageWarning({
        show: true,
        message: "Failed to save calculation. Please try again."
      });
    }
  };

  // Load user preferences on initial mount
  useEffect(() => {
    if (initialLoadRef.current && !isLoading && preferences.defaultDivision) {
      setDivision(preferences.defaultDivision);
      initialLoadRef.current = false;
    }
  }, [preferences, isLoading, H]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Calculator</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen ref={contentRef}>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Calculator</IonTitle>
            <IonButtons slot="end">
              <OnlineIndicator />
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonGrid>
          <IonRow>
            <IonCol>
              <IonSegment 
                value={division}
                disabled={state === "results"}
                scrollable={false}
                swipeGesture={true}
                onIonChange={e => {
                  const newDivision = e.detail.value as 'A Division' | 'B Division';
                  setDivision(newDivision);
                  
                  // If user has started input, this change may affect validation
                  if (hasStartedInput) {
                    // Validation will be automatically re-checked via the useInputValidation hook
                  }
                }}
              >
                <IonSegmentButton value="A Division">
                  <IonLabel>A Division</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="B Division">
                  <IonLabel>B Division</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonSegment 
                value={trackType}
                disabled={state === "results"}
                scrollable={false}
                swipeGesture={true}
                onIonChange={e => setTrackType(e.detail.value as 'curve' | 'tangent')}
              >
                <IonSegmentButton value="tangent">
                  <IonLabel>Tangent Track</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="curve">
                  <IonLabel>Curved Track</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonSegment 
                value={direction}
                disabled={state === "results"}
                scrollable={false}
                swipeGesture={true}
                onIonChange={e => setDirection(e.detail.value as 'inside' | 'outside')}
              >
                <IonSegmentButton value="inside">
                  <IonLabel>
                    {trackType === `curve` ? `Inside of Curve` : `Side of Lower Rail`}
                  </IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="outside">
                  <IonLabel>
                    {trackType === `curve` ? `Outside of Curve` : `Side of Higher Rail`}
                  </IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonInput 
                label="Height from T.O.R."
                labelPlacement="stacked"
                fill='outline'
                type="number"
                step={hStep}
                inputMode='decimal'
                disabled={state === 'results'}
                min={-0.5}
                max={1000}
                value={H === null ? '' : H}
                placeholder='0.000'
                required={true}
                className={`${validation.fieldShakes.has('H') ? 'field-validation-shake' : ''} ${validation.invalidFields.has('H') || validation.missingFieldsForCalculate.has('H') || validation.torGorError.hasError ? 'input-validation-error' : ''}`.trim()}
                onIonInput={(e) => {
                  setHasStartedInput(true);
                  createNumericInputHandler(setH, -0.5, 1000, 3)(e);
                  // Clear validation errors immediately as user types valid input
                  validation.clearValidationOnChange('H', (e as any)?.detail?.value);
                }}
                onIonChange={(e) => {
                  // Clear validation errors when input becomes valid
                  if (H !== null) {
                    validation.clearFieldValidation('H');
                    validation.clearCalculateFieldError('H');
                  }
                  // Also use the change-based validation clearing for consistency
                  validation.clearValidationOnChange('H', (e as any)?.detail?.value || H);
                  // Note: TOR/GOR compatibility validation will be re-evaluated automatically via useEffect in the hook
                }}
              >
                <IonButtons slot="end">
                  <IonButton 
                    className="toggle-sign-btn"
                    size="small" 
                    fill="clear" 
                    disabled={state === 'results' || H === null}
                    onClick={() => toggleSignHandler(setH, H, -0.5, 1000, triggerToggleWarningH)}
                  >
                    +/-
                  </IonButton>
                </IonButtons>
              </IonInput>
            </IonCol>
            <IonCol>
              <IonInput 
                label="Distance from G.O.R."
                labelPlacement="stacked"
                fill='outline'
                type="number"
                step={dStep}
                inputMode='decimal'
                disabled={state === 'results'}
                min={-28.25}
                max={1000}
                value={D === null ? '' : D}
                placeholder='0.000'
                required={true}
                className={`${validation.fieldShakes.has('D') ? 'field-validation-shake' : ''} ${validation.invalidFields.has('D') || validation.missingFieldsForCalculate.has('D') || validation.torGorError.hasError ? 'input-validation-error' : ''}`.trim()}
                onIonInput={(e) => {
                  setHasStartedInput(true);
                  createNumericInputHandler(setD, -28.25, 1000, 3)(e);
                  // Clear validation errors immediately as user types valid input
                  validation.clearValidationOnChange('D', (e as any)?.detail?.value);
                }}
                onIonChange={(e) => {
                  // Clear validation errors when input becomes valid
                  if (D !== null) {
                    validation.clearFieldValidation('D');
                    validation.clearCalculateFieldError('D');
                  }
                  // Also use the change-based validation clearing for consistency
                  validation.clearValidationOnChange('D', (e as any)?.detail?.value || D);
                  // Note: TOR/GOR compatibility validation will be re-evaluated automatically via useEffect in the hook
                }}
                onBlur={() => {
                  // Only show the alert if D is not null and in the special range
                  if (D !== null && D <= 0 && D >= -28.25) {
                    setShowSpecialGorAlert(true);
                  }
                }}
              >
                <IonButtons slot="end">
                  <IonButton 
                    className="toggle-sign-btn"
                    size="small" 
                    fill="clear" 
                    disabled={state === 'results' || D === null}
                    onClick={() => toggleSignHandler(setD, D, -28.25, 1000, triggerToggleWarningD)}
                  >
                    +/-
                  </IonButton>
                </IonButtons>
              </IonInput>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonInput 
                label="Middle Ordinate"
                labelPlacement="stacked"
                fill='outline'
                type="number"
                step={moStep}
                inputMode='decimal'
                disabled={trackType !== `curve` || state === 'results' || isSpecialGorRange}
                min={0}
                max={1000}
                value={MO === null ? '' : MO}
                placeholder='0.000'
                required={true}
                className={`${validation.fieldShakes.has('MO') ? 'field-validation-shake' : ''} ${
                  (trackType !== 'curve' || state === 'results' || isSpecialGorRange) 
                    ? '' 
                    : validation.missingFieldsForCalculate.has('MO') ? 'input-validation-error' : ''
                }`.trim()}
                onIonInput={(e) => {
                  setHasStartedInput(true);
                  createNumericInputHandler(setMO, 0, 1000, 3)(e);
                  // Clear validation errors immediately as user types valid input
                  validation.clearValidationOnChange('MO', (e as any)?.detail?.value);
                }}
                onIonChange={(e) => {
                  // Clear validation errors when field gets valid content
                  if (MO !== null) {
                    validation.clearFieldValidation('MO');
                    validation.clearCalculateFieldError('MO');
                  }
                  // Also use the change-based validation clearing for consistency
                  validation.clearValidationOnChange('MO', e.detail.value);
                }}
              />
              
            </IonCol>
            <IonCol>
              <IonInput 
                label="Super Elevation"
                labelPlacement="stacked"
                fill='outline'
                type="number"
                step={superStep}
                inputMode='decimal'
                disabled={state === 'results' || isSpecialGorRange}
                min={0}
                max={1000}
                value={SUPER === null ? '' : SUPER}
                placeholder='0.000'
                required={true}
                className={`${validation.fieldShakes.has('SUPER') ? 'field-validation-shake' : ''} ${
                  (state === 'results' || isSpecialGorRange) 
                    ? '' 
                    : validation.missingFieldsForCalculate.has('SUPER') ? 'input-validation-error' : ''
                }`.trim()}
                onIonInput={(e) => {
                  setHasStartedInput(true);
                  createNumericInputHandler(setSUPER, 0, 1000, 3)(e);
                  // Clear validation errors immediately as user types valid input
                  validation.clearValidationOnChange('SUPER', (e as any)?.detail?.value);
                }}
                onIonChange={(e) => {
                  // Clear validation errors when field gets valid content
                  if (SUPER !== null) {
                    validation.clearFieldValidation('SUPER');
                    validation.clearCalculateFieldError('SUPER');
                  }
                  // Also use the change-based validation clearing for consistency
                  validation.clearValidationOnChange('SUPER', e.detail.value);
                }}
              />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonButton
                expand="block"
                color="secondary"
                strong={true}
                type={state === 'input' ? 'button' : 'reset'}
                aria-label={state === 'input' 
                  ? 'Calculate clearance values'
                  : 'Reset options'
                }
                onClick={handleResetClick}
              >{state === 'input' ? 'Calculate' : 'Reset...'}</IonButton>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonItem
                lines="none"
                disabled={state === 'input'}
                className={state === 'input' ? 'output-disabled' : 'output-enabled'}
              >
                <IonLabel>
                    <h3>Radius</h3>
                    <p>{`${R.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
            </IonCol>
            <IonCol>
              <IonItem
                lines="none"
                disabled={state === 'input'}
                className={state === 'input' ? 'output-disabled' : 'output-enabled'}
              >
                <IonLabel>
                    <h3>Super Elev. Excess</h3>
                    <p>{`${SE.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonItem
                lines="none"
                disabled={state === 'input'}
                className={state === 'input' ? 'output-disabled' : 'output-enabled'}
              >
                <IonLabel>
                    <h3>End Excess</h3>
                    <p>{`${EE.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
            </IonCol>
            <IonCol>
              <IonItem
                lines="none"
                disabled={state === 'input'}
                className={state === 'input' ? 'output-disabled' : 'output-enabled'}
              >
                <IonLabel>
                    <h3>Center Excess</h3>
                    <p>{`${CE.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonItem
                lines="none"
                disabled={state === 'input'}
                className={state === 'input' ? 'output-disabled' : 'output-enabled'}
              >
                <IonLabel>
                    <h3>LLLE (without Excess)</h3>
                    <p>{`${LLLEMinReq.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
            </IonCol>
            <IonCol>
              <IonItem
                lines="none"
                disabled={state === 'input'}
                className={state === 'input' ? 'output-disabled' : 'output-enabled'}
              >
                <IonLabel>
                    <h3>LLLE (with Excess)</h3>
                    <p>{`${LLLEClearance.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12">
              <IonItem
                lines="none"
                color={state === 'input' ? 'light' : (clearance >= 0 ? 'safelight' : 'dangerlight')}
                disabled={state === 'input'}
                className={`calculated-clearance ${state === 'input' ? 'output-disabled' : 'output-enabled'}`}
              >
                <IonLabel>
                    <h3>Calculated Clearance</h3>
                    <p>{`${clearance.toFixed(3)} in.`}</p>
                  </IonLabel>
                </IonItem>
              </IonCol>
          </IonRow>

          <IonRow>
            <IonCol>
              <IonButton
                expand="block"
                color={state === 'results' ? "warning" : "light"}
                strong={true}
                disabled={state === 'input'}
                onClick={openSaveDialog}
              >
                Save This Calculation
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>

      {/* Save calculation modal */}
      <IonModal 
        isOpen={showSaveDialog} 
        onDidDismiss={() => setShowSaveDialog(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowSaveDialog(false)}>Cancel</IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton strong={true} onClick={handleSaveCalculation}>Save</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <IonItem lines="none" className="ion-padding-bottom">
            <IonLabel position="stacked" className="ion-padding-bottom">Calculation Name</IonLabel>
            <IonInput
              value={calculationName}
              placeholder={`Calculation ${savedCalculations.length + 1}`}
              onIonInput={(e) => setCalculationName(e.detail.value || '')}
            />
          </IonItem>
          <IonItem lines="none" className="ion-padding-bottom">
            <IonLabel position="stacked" className="ion-padding-bottom">Track (Optional)</IonLabel>
            <IonInput
              value={saveTrack}
              placeholder="e.g., E2"
              onIonInput={(e) => setSaveTrack(e.detail.value || '')}
            />
          </IonItem>
        </IonContent>
      </IonModal>

      {/* Ballasted Track Alert */}
      <IonAlert
        isOpen={showBallastedAlert && state === 'input'}
        onDidDismiss={() => setShowBallastedAlert(false)}
        header="Ballasted Track"
        message="Is this ballasted track?"
        buttons={[
          {
            text: 'No',
            role: 'cancel',
            handler: () => handleBallastedResponse(false)
          },
          {
            text: 'Yes',
            handler: () => handleBallastedResponse(true)
          }
        ]}
      />

      {/* Vertical Curve Alert */}
      <IonAlert
        isOpen={showVerticalCurveAlert && state === 'input'}
        onDidDismiss={() => setShowVerticalCurveAlert(false)}
        header="Vertical Curvature"
        message="Is there a vertical curve on the track?"
        buttons={[
          {
            text: 'No',
            role: 'cancel',
            handler: () => handleVerticalCurveResponse(false)
          },
          {
            text: 'Yes',
            handler: () => handleVerticalCurveResponse(true)
          }
        ]}
      />

      {/* Adjustment Information Alert */}
      <IonAlert
        isOpen={showAdjustmentInfoAlert && state === 'input'}
        onDidDismiss={() => setShowAdjustmentInfoAlert(false)}
        header="Note on Required Height"
        message={getAdjustmentMessage()}
        buttons={[
          {
            text: 'Continue',
            handler: () => handleAdjustmentInfoContinue()
          }
        ]}
      />

      {/* Special GOR Range Alert */}
      <IonAlert
        isOpen={showSpecialGorAlert && state === 'input'}
        onDidDismiss={() => setShowSpecialGorAlert(false)}
        header="Above Train Calculation"
        message="This location is within the gauge of rail. Would you like to continue with this above train calculation?"
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            handler: () => {
              setD(null);
              setShowSpecialGorAlert(false);
            }
          },
          {
            text: 'Continue',
            handler: () => {
              setShowSpecialGorAlert(false);
            }
          }
        ]}
      />

      {/* TOR/GOR Validation Toast */}
      <IonToast
        isOpen={showTorGorToast}
        onDidDismiss={() => setShowTorGorToast(false)}
        message={
          "Height exceeds maximum for wayside measurements. If this is wayside, try a lower height. If this is above train, use a distance within the gauge of rail."
        }
        position="top"
        positionAnchor="calculator-content"
        duration={0}
        cssClass="tor-gor-validation-toast"
        buttons={[
          {
            icon: close,
            role: 'cancel',
            side: 'end'
          }
        ]}
        icon={warningOutline}
      />

      {/* Reset Options Action Sheet */}
      <IonActionSheet
        isOpen={showResetActionSheet}
        onDidDismiss={() => setShowResetActionSheet(false)}
        header="Edit these measurements or clear all?"
        buttons={[
          {
            text: 'Edit Measurements',
            data: {
              subtitle: 'Keep current values'
            },
            handler: () => {
              handleEditMeasurements();
              return true;
            }
          },
          {
            text: 'Clear Measurements',
            data: {
              subtitle: 'Clear everything'
            },
            handler: () => {
              updateCalcs();
              return true;
            }
          },
          {
            text: 'Cancel',
            role: 'cancel',
            handler: () => {
              return true;
            }
          }
        ]}
      />

    </IonPage>
  );
};

export default Calculator;
